import { store } from "quasar/wrappers";
import { InjectionKey } from "vue";
import {
  createStore,
  Store as VuexStore,
  useStore as vuexUseStore,
} from "vuex";

// import example from './module-example';
import SettingsModule from "./settings";
import { SettingsStateInterface } from "./settings/state";
import UserModule from "./user";
import { UserStateInterface } from "./user/state";
import CubeModule from "src/modules/cube/store/cubes";
import { CubeStateInterface } from "src/modules/cube/store/cubes/state";
import KbModule from "src/modules/kb/store/kb";
import { KbStateInterface } from "src/modules/kb/store/kb/state";
import DatasetModule from "src/modules/dataset/store/dataset";
import { DatasetStateInterface } from "src/modules/dataset/store/dataset/state";
import IdomModule from "src/modules/idom/store/idom";
import { IdomStateInterface } from "src/modules/idom/store/idom/state";
import IdomSettingsModule from "src/modules/idom-settings/store/settings";
import { IIdomSettingsStateInterface } from "src/modules/idom-settings/store/settings/state";
import ChatModule from "src/modules/chat/store/chat";
import { ChatStateInterface } from "src/modules/chat/store/chat/state";
import EventsModule from "src/modules/chat/store/events";
import { EventsStateInterface } from "src/modules/chat/store/events/state";
import EventsUtilsModule from "src/modules/chat/store/events-utils";
import { EventsUtilsStateInterface } from "src/modules/chat/store/events-utils/state";
import SessionsUtilsModule from "src/modules/chat/store/sessions-utils";
import { SessionsUtilsStateInterface } from "src/modules/chat/store/sessions-utils/state";
import { LomsStateInterface } from "src/modules/loms/store/loms/state";
import LomsModule from "src/modules/loms/store/loms";
import { IMediaPlansStateInterface } from "src/modules/mediaplans/store/mediaplans/state";
import MediaplansModule from "src/modules/mediaplans/store/mediaplans";
import { IdomMemStateInterface } from "src/modules/idom-mem/store/idom-mem/state";
import IdomMemModule from "src/modules/idom-mem/store/idom-mem";
import { IdomReactionStateInterface } from "src/modules/idom-reaction/store/idom-reaction/state";
import IdomReactionModule from "src/modules/idom-reaction/store/idom-reaction";

export interface StateInterface {
  // Define your own store structure, using submodules if needed
  // example: ExampleStateInterface;
  // Declared as unknown to avoid linting issue. Best to strongly type as per the line above.
  settings: SettingsStateInterface;
  user: UserStateInterface;
  cubes: CubeStateInterface;
  kb: KbStateInterface;
  dataset: DatasetStateInterface;
  idom: IdomStateInterface;
  mediaplans: IMediaPlansStateInterface;
  idomSettings: IIdomSettingsStateInterface;
  loms: LomsStateInterface;
  chat: ChatStateInterface;
  events: EventsStateInterface;
  eventsUtils: EventsUtilsStateInterface;
  sessionsUtils: SessionsUtilsStateInterface;
  idomMem: IdomMemStateInterface;
  idomReaction: IdomReactionStateInterface;
}

// provide typings for `this.$store`
declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    $store: VuexStore<StateInterface>;
  }
}

// provide typings for `useStore` helper
export const storeKey: InjectionKey<VuexStore<StateInterface>> =
  Symbol("vuex-key");

export default store(function (/* { ssrContext } */) {
  const Store = createStore<StateInterface>({
    modules: {
      settings: SettingsModule,
      user: UserModule,
      cubes: CubeModule,
      kb: KbModule,
      dataset: DatasetModule,
      idom: IdomModule,
      chat: ChatModule,
      events: EventsModule,
      eventsUtils: EventsUtilsModule,
      sessionsUtils: SessionsUtilsModule,
      loms: LomsModule,
      mediaplans: MediaplansModule,
      idomSettings: IdomSettingsModule,
      idomMem: IdomMemModule,
      idomReaction: IdomReactionModule,
    },
    // enable strict mode (adds overhead!)
    // for dev mode and --debug builds only
    strict: !!process.env.DEBUGGING,
  });

  return Store;
});

export function useStore() {
  return vuexUseStore(storeKey);
}
